// SPDX-License-Identifier: AGPL-3.0-or-later

import {Routes} from '@app/app/Routes';
import type {ShopCollection} from '@app/features/shop/utils/ShopCollections';
import type {ShopBundleEntry, ShopItemEntry} from '@app/features/shop/utils/ShopEntries';
import {priceCurrency, shownCents, unsoldPrice} from '@app/features/shop/utils/ShopPriceFormat';

const PROMO_COUNT = 2;

// A set page is the shop route with the set's bundle ID in the query, so the back button and links work as usual.
export const SHOP_SET_QUERY = 'set';

export function shopSetPath(bundleId: string): string {
	return `${Routes.SHOP}?${new URLSearchParams({[SHOP_SET_QUERY]: bundleId})}`;
}

// The shop opened on its room character filter, for links from a 3D room.
export const SHOP_FILTER_QUERY = 'filter';
export const SHOP_ROOM_PATH = `${Routes.SHOP}?${new URLSearchParams({[SHOP_FILTER_QUERY]: 'room'})}`;

// A personal username offer's link: the shop opened on that offer's dialog.
export const SHOP_OFFER_QUERY = 'offer';

// What the bundle saves against its items bought one by one, in whole percent, from the catalog's own prices (the
// campaign price on both sides while one runs). Null when an item has no price of its own, one the viewer is not sold or
// priced in another currency, or nothing is saved.
export function bundleSavingsPercent(
	entry: ShopBundleEntry,
	items: ReadonlyArray<ShopItemEntry>,
	now: number,
): number | null {
	const prices = new Map(items.map(({item, price}) => [item.id, price]));
	let total = 0;
	for (const item of entry.bundle.items) {
		const price = prices.get(item.id);
		if (!price || unsoldPrice(price) || priceCurrency(price) !== priceCurrency(entry.price)) return null;
		total += shownCents(price, now);
	}
	const cents = shownCents(entry.price, now);
	if (total <= 0 || cents <= 0) return null;
	// Rounded down: a paid bundle never reads 100%, and no saving is promised beyond the prices.
	const percent = Math.floor(((total - cents) * 100) / total);
	return percent > 0 ? percent : null;
}

// Art shipped with the client, matched by the bundle's name: the fallback for a set without admin art (the bundle's
// banner, uploaded from admin and served by the CDN). Drop an entry once its set has admin art in production.
const SET_ART_KEYS: ReadonlyArray<[RegExp, string]> = [
	[/angel/i, 'angel-wings'],
	[/kuzgun|raven/i, 'kuzgun'],
	[/sakura/i, 'sakura'],
	[/sonbahar|payız|payiz|autumn/i, 'sonbahar'],
	[/neon/i, 'neon'],
	[/kedi|cat\b/i, 'kedi'],
	[/ejder|dragon/i, 'kara-ejder'],
	[/yarasa/i, 'gece-yarasasi'],
];

// Set names are the shop's own product names: Turkish, and English for every other language, as the admin names the
// items; same move as the art keys above.
const SET_NAMES: Record<string, string> = {
	'angel-wings': 'Angel Wings',
	kuzgun: 'Kuzgun',
	sakura: 'Sakura',
	sonbahar: 'Payız',
	neon: 'Neon',
	kedi: 'Kedi',
	'kara-ejder': 'Kara Ejder',
	'gece-yarasasi': 'Gece Yarasası',
};
const SET_NAMES_EN: Record<string, string> = {
	'angel-wings': 'Angel Wings',
	kuzgun: 'Raven Wings',
	sakura: 'Sakura',
	sonbahar: 'Autumn',
	neon: 'Neon',
	kedi: 'Cat',
	'kara-ejder': 'Black Dragon',
	'gece-yarasasi': 'Night Bat',
};

export function shopSetName(key: string, locale: string | undefined): string {
	const names = locale?.toLowerCase().startsWith('tr') ? SET_NAMES : SET_NAMES_EN;
	return names[key] ?? key;
}

export function shopSetArtKey(name: string): string | null {
	return SET_ART_KEYS.find(([pattern]) => pattern.test(name))?.[1] ?? null;
}

// A set's page: its bundle, then every item of the bundle that is on display on its own, in bundle order. Built from
// the bundle itself, not from the front page's rows, which show an item shared by two sets only once.
export function buildShopSetCollection(
	entry: ShopBundleEntry,
	items: ReadonlyArray<ShopItemEntry>,
): ShopCollection & {bundle: ShopBundleEntry} {
	const onDisplay = new Map(items.map((item) => [item.item.id, item]));
	return {
		id: entry.sku,
		bundle: entry,
		entries: [entry, ...entry.bundle.items.flatMap((item) => onDisplay.get(item.id) ?? [])],
	};
}

export interface ShopFrontPlan {
	// The hero's set: the first collection that is a set.
	featured: ShopCollection | null;
	// The next sets, as tiles that open their set page.
	promos: Array<ShopCollection>;
	// Everything else, as rows, in catalog order.
	rows: Array<ShopCollection>;
}

export function planShopFront(collections: ReadonlyArray<ShopCollection>): ShopFrontPlan {
	const sets = collections.filter((collection) => collection.bundle !== null);
	const featured = sets[0] ?? null;
	const promos = sets.slice(1, 1 + PROMO_COUNT);
	const taken = new Set([featured, ...promos]);
	return {featured, promos, rows: collections.filter((collection) => !taken.has(collection))};
}
